/**
 * useEmailData Hook
 *
 * Manages email data fetching and state for accounts, folders, threads.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import emailApi, {
  EmailAccount,
  EmailFolder,
  EmailThreadPreview,
  EmailDraft,
  ThreadListResponse,
  AIDashboardResponse,
  dashboardApi,
  draftsApi,
} from '@/services/emailApi';

export type ThreadFilterMode = 'all' | 'unread' | 'actionable';

interface UseEmailDataOptions {
  accountId?: string;
  folderId?: string;
  searchQuery?: string;
  filterMode?: ThreadFilterMode;
}

export function useEmailData(options: UseEmailDataOptions = {}) {
  const { accountId, folderId, searchQuery, filterMode = 'all' } = options;

  // Accounts state
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [accountFolders, setAccountFolders] = useState<Record<string, EmailFolder[]>>({});
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(accountId || null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId || null);

  // Threads state
  const [threads, setThreads] = useState<EmailThreadPreview[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Drafts state
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  // AI Dashboard state
  const [aiDashboard, setAiDashboard] = useState<AIDashboardResponse | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Ref for tracking if initial load is done
  const initialLoadDone = useRef(false);

  // Refs to capture initial URL params (don't re-run when they change)
  const initialAccountId = useRef(accountId);
  const initialFolderId = useRef(folderId);

  // Load accounts on mount (only runs once)
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const accts = await emailApi.accounts.list();
      setAccounts(accts);

      // Expand all accounts and load their folders
      if (accts.length > 0) {
        const newExpanded = new Set(accts.map(a => a.id));
        setExpandedAccounts(newExpanded);

        // Load folders for all accounts
        const foldersMap: Record<string, EmailFolder[]> = {};
        await Promise.all(accts.map(async (acc) => {
          try {
            const accFolders = await emailApi.folders.list(acc.id);
            foldersMap[acc.id] = accFolders;
          } catch (err) {
            console.error(`Failed to load folders for ${acc.email}:`, err);
            foldersMap[acc.id] = [];
          }
        }));
        setAccountFolders(foldersMap);

        // Select account based on initial URL param or first account
        const targetAccountId = initialAccountId.current || accts.find(a => a.is_primary || a.is_preferred)?.id || accts[0].id;
        setSelectedAccountId(targetAccountId);

        // Select folder based on initial URL param or inbox
        if (initialFolderId.current) {
          setSelectedFolderId(initialFolderId.current);
        } else {
          const inbox = foldersMap[targetAccountId]?.find(f => f.folder_type === 'inbox');
          if (inbox) setSelectedFolderId(inbox.id);
        }
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, []); // No dependencies - only runs once on mount

  // Load threads for selected account/folder
  const loadThreads = useCallback(async (pageNum: number = 1, isInitialLoad: boolean = false) => {
    if (!selectedAccountId) return;

    try {
      if (isInitialLoad) {
        setLoadingThreads(true);
      } else {
        setIsLoadingMore(true);
      }

      const response: ThreadListResponse = await emailApi.threads.list({
        account_id: selectedAccountId,
        folder_id: selectedFolderId || undefined,
        page: pageNum,
        limit: 50,
        search: searchQuery || undefined,
        filter: filterMode !== 'all' ? filterMode : undefined,
      });

      if (isInitialLoad) {
        setThreads(response.threads);
      } else {
        setThreads(prev => [...prev, ...response.threads]);
      }

      setPage(pageNum);
      setTotalCount(response.total_count);
      setHasNextPage(pageNum < response.total_pages);
    } catch (err) {
      console.error('Failed to load threads:', err);
    } finally {
      setLoadingThreads(false);
      setIsLoadingMore(false);
    }
  }, [selectedAccountId, selectedFolderId, searchQuery, filterMode]);

  // Load more threads (for infinite scroll)
  const loadMoreThreads = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) return;
    await loadThreads(page + 1, false);
  }, [page, hasNextPage, isLoadingMore, loadThreads]);

  // Load AI dashboard
  const loadAIDashboard = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      setLoadingDashboard(true);
      const data = await dashboardApi.get(selectedAccountId, 20);
      setAiDashboard(data);
    } catch (err) {
      console.error('Failed to load AI dashboard:', err);
      setAiDashboard(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [selectedAccountId]);

  // Load drafts
  const loadDrafts = useCallback(async (accId: string) => {
    try {
      setLoadingDrafts(true);
      const draftList = await draftsApi.list(accId);
      setDrafts(draftList);
    } catch (err) {
      console.error('Failed to load drafts:', err);
      setDrafts([]);
    } finally {
      setLoadingDrafts(false);
    }
  }, []);

  // Toggle account expansion in tree
  const toggleAccountExpand = useCallback((accId: string) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(accId)) {
        next.delete(accId);
      } else {
        next.add(accId);
      }
      return next;
    });
  }, []);

  // Select a folder
  const selectFolder = useCallback((accId: string, foldId: string) => {
    // Don't do anything if the same folder is already selected
    if (selectedAccountId === accId && selectedFolderId === foldId) {
      return;
    }

    if (selectedAccountId !== accId) {
      setSelectedAccountId(accId);
    }
    setSelectedFolderId(foldId);
    setPage(1);
    setThreads([]);
  }, [selectedAccountId, selectedFolderId]);

  // Update thread read status locally
  const markThreadAsRead = useCallback((threadId: string) => {
    setThreads(prev => prev.map(t =>
      t.id === threadId ? { ...t, unread_count: 0 } : t
    ));
    // Also update folder unread count
    if (selectedAccountId && selectedFolderId) {
      setAccountFolders(prev => ({
        ...prev,
        [selectedAccountId]: prev[selectedAccountId]?.map(f =>
          f.id === selectedFolderId
            ? { ...f, unread_count: Math.max(0, (f.unread_count || 0) - 1) }
            : f
        ) || []
      }));
    }
  }, [selectedAccountId, selectedFolderId]);

  // Add account handler
  const addAccount = useCallback((account: EmailAccount) => {
    setAccounts(prev => [...prev, account]);
    setSelectedAccountId(account.id);
  }, []);

  // Delete draft locally
  const deleteDraftLocal = useCallback((draftId: string) => {
    setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
  }, []);

  // Initial load
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Update selection when URL params change
  useEffect(() => {
    if (!initialLoadDone.current) return;

    if (accountId && accountId !== selectedAccountId) {
      setSelectedAccountId(accountId);
    }
    if (folderId && folderId !== selectedFolderId) {
      setSelectedFolderId(folderId);
    }
  }, [accountId, folderId, selectedAccountId, selectedFolderId]);

  // Load threads when folder or filter changes
  useEffect(() => {
    if (selectedAccountId && initialLoadDone.current) {
      setPage(1);
      setThreads([]);
      loadThreads(1, true);
    }
  }, [selectedAccountId, selectedFolderId, filterMode, loadThreads]);

  // Load AI dashboard when account is selected
  useEffect(() => {
    if (selectedAccountId && initialLoadDone.current) {
      loadAIDashboard();
    }
  }, [selectedAccountId, loadAIDashboard]);

  // Get folders for currently selected account
  const folders = selectedAccountId ? (accountFolders[selectedAccountId] || []) : [];

  // Check if we have AI dashboard data
  const hasAIDashboard = aiDashboard && (
    aiDashboard.urgent.length > 0 ||
    aiDashboard.unread_with_action.length > 0 ||
    aiDashboard.unread.length > 0 ||
    aiDashboard.read_with_action.length > 0
  );

  return {
    // Account state
    accounts,
    accountFolders,
    expandedAccounts,
    selectedAccountId,
    selectedFolderId,
    folders,

    // Thread state
    threads,
    page,
    totalCount,
    hasNextPage,

    // Draft state
    drafts,
    loadingDrafts,

    // AI Dashboard state
    aiDashboard,
    hasAIDashboard,
    loadingDashboard,

    // Loading states
    loading,
    loadingThreads,
    isLoadingMore,

    // Actions
    loadAccounts,
    loadThreads,
    loadMoreThreads,
    loadAIDashboard,
    loadDrafts,
    toggleAccountExpand,
    selectFolder,
    markThreadAsRead,
    addAccount,
    deleteDraftLocal,
  };
}

export default useEmailData;
